export const componentKinds = ['profile', 'game', 'task', 'data-view'];

export const componentPlacements = ['profile', 'dashboard', 'both'];

export const componentLayoutWidths = ['full', 'half', 'compact'];

export const defaultComponentTemplates = [
  {
    id: 'hero-bio',
    creatorId: 'platform',
    name: 'Hero Bio',
    description: 'A profile opening block with display name, tagline, and a short biography.',
    kind: 'profile',
    category: 'profile',
    schema: {
      fields: [
        { name: 'tagline', type: 'text', label: 'Tagline' },
        { name: 'bio', type: 'markdown', label: 'Bio' },
      ],
    },
    defaultSettings: {
      tagline: 'A living autobiography, shaped one memory at a time.',
      bio: 'Start with a short introduction.',
    },
    defaultVisibility: 'public',
    placement: 'profile',
    defaultLayoutWidth: 'full',
  },
  {
    id: 'custom-markdown',
    creatorId: 'platform',
    name: 'Custom Markdown',
    description: 'A blank profile component for text, links, lists, and personal notes.',
    kind: 'profile',
    category: 'profile',
    schema: {
      fields: [
        { name: 'content', type: 'markdown', label: 'Content' },
      ],
    },
    defaultSettings: {
      content: 'Write something personal here.',
    },
    defaultVisibility: 'private',
    placement: 'profile',
    defaultLayoutWidth: 'half',
  },
  {
    id: 'story-list',
    creatorId: 'platform',
    name: 'Story List',
    description: 'A profile block that links to your visible stories.',
    kind: 'data-view',
    category: 'content',
    schema: {
      fields: [
        { name: 'limit', type: 'number', label: 'Story limit' },
      ],
    },
    defaultSettings: {
      limit: 4,
    },
    defaultVisibility: 'public',
    placement: 'profile',
    defaultLayoutWidth: 'half',
    dataSource: 'stories',
  },
  {
    id: 'timeline-list',
    creatorId: 'platform',
    name: 'Timeline List',
    description: 'A profile block that shows visible timeline moments.',
    kind: 'data-view',
    category: 'content',
    schema: {
      fields: [
        { name: 'limit', type: 'number', label: 'Timeline limit' },
      ],
    },
    defaultSettings: {
      limit: 5,
    },
    defaultVisibility: 'public',
    placement: 'profile',
    defaultLayoutWidth: 'half',
    dataSource: 'timeline',
  },
  {
    id: 'legacy-widgets',
    creatorId: 'platform',
    name: 'Legacy Widgets',
    description: 'A transition block for older profile widgets while components take over.',
    kind: 'data-view',
    category: 'profile',
    schema: {
      fields: [],
    },
    defaultSettings: {},
    defaultVisibility: 'public',
    placement: 'profile',
    defaultLayoutWidth: 'full',
    dataSource: 'legacy-widgets',
  },
  {
    id: 'daily-reflection',
    creatorId: 'platform',
    name: 'Daily Reflection',
    description: 'A small daily task that asks one autobiographical question.',
    kind: 'task',
    category: 'daily',
    schema: {
      fields: [
        { name: 'prompt', type: 'text', label: 'Prompt' },
        { name: 'streakGoal', type: 'number', label: 'Streak goal' },
      ],
    },
    defaultSettings: {
      prompt: 'What mattered today?',
      streakGoal: 7,
    },
    defaultVisibility: 'private',
    placement: 'dashboard',
  },
  {
    id: 'habit-check-in',
    creatorId: 'platform',
    name: 'Habit Check-In',
    description: 'A quick daily check-in for tracking one habit or life practice.',
    kind: 'task',
    category: 'daily',
    schema: {
      fields: [
        { name: 'habit', type: 'text', label: 'Habit' },
        { name: 'question', type: 'text', label: 'Question' },
      ],
    },
    defaultSettings: {
      habit: 'Write one memory',
      question: 'Did you show up today?',
    },
    defaultVisibility: 'private',
    placement: 'dashboard',
  },
  {
    id: 'fitness-log',
    creatorId: 'platform',
    name: 'Fitness Log',
    description: 'Track workouts, movement, recovery, or body goals as part of your life record.',
    kind: 'task',
    category: 'fitness',
    schema: {
      fields: [
        { name: 'activity', type: 'text', label: 'Activity' },
        { name: 'prompt', type: 'text', label: 'Prompt' },
      ],
    },
    defaultSettings: {
      activity: 'Workout or movement',
      prompt: 'What did you do, and how did it feel?',
    },
    defaultVisibility: 'private',
    placement: 'dashboard',
    dataSource: 'fitness-manual',
  },
  {
    id: 'gaming-session',
    creatorId: 'platform',
    name: 'Gaming Session',
    description: 'Log games, wins, losses, clips, rank progress, or sessions with friends.',
    kind: 'game',
    category: 'gaming',
    schema: {
      fields: [
        { name: 'game', type: 'text', label: 'Game' },
        { name: 'prompt', type: 'text', label: 'Prompt' },
      ],
    },
    defaultSettings: {
      game: 'Current game',
      prompt: 'What happened in this session?',
    },
    defaultVisibility: 'private',
    placement: 'dashboard',
    dataSource: 'gaming-manual',
  },
  {
    id: 'creative-session',
    creatorId: 'platform',
    name: 'Creative Session',
    description: 'Capture what you made, practiced, edited, drafted, or shipped.',
    kind: 'task',
    category: 'creative',
    schema: {
      fields: [
        { name: 'medium', type: 'text', label: 'Medium' },
        { name: 'prompt', type: 'text', label: 'Prompt' },
      ],
    },
    defaultSettings: {
      medium: 'Creative work',
      prompt: 'What did you create or improve today?',
    },
    defaultVisibility: 'private',
    placement: 'dashboard',
    dataSource: 'creative-manual',
  },
  {
    id: 'writing-log',
    creatorId: 'platform',
    name: 'Writing Log',
    description: 'Track diary entries, essays, story drafts, captions, or writing streaks.',
    kind: 'task',
    category: 'writing',
    schema: {
      fields: [
        { name: 'project', type: 'text', label: 'Project' },
        { name: 'prompt', type: 'text', label: 'Prompt' },
      ],
    },
    defaultSettings: {
      project: 'Diary or story',
      prompt: 'What did you write, and what should future you remember?',
    },
    defaultVisibility: 'private',
    placement: 'dashboard',
  },
  {
    id: 'mood-energy-check',
    creatorId: 'platform',
    name: 'Mood & Energy Check',
    description: 'Log your mood, energy, and context so patterns become visible over time.',
    kind: 'task',
    category: 'wellness',
    schema: {
      fields: [
        { name: 'prompt', type: 'text', label: 'Prompt' },
        { name: 'scaleLabel', type: 'text', label: 'Scale label' },
      ],
    },
    defaultSettings: {
      prompt: 'How are you feeling today?',
      scaleLabel: 'Mood / energy score',
    },
    defaultVisibility: 'private',
    placement: 'dashboard',
  },
  {
    id: 'daily-stats',
    creatorId: 'platform',
    name: 'Daily Stats',
    description: 'A public or friends-only block for sharing selected task streaks and history.',
    kind: 'data-view',
    category: 'daily',
    schema: {
      fields: [
        { name: 'title', type: 'text', label: 'Title' },
        { name: 'showStreak', type: 'boolean', label: 'Show streak' },
      ],
    },
    defaultSettings: {
      title: 'Daily Progress',
      showStreak: true,
    },
    defaultVisibility: 'friends',
    placement: 'profile',
    defaultLayoutWidth: 'half',
    dataSource: 'task-runs',
  },
];

export function normalizeComponentTemplate(template) {
  return {
    ...template,
    kind: componentKinds.includes(template.kind) ? template.kind : 'profile',
    category: template.category || 'custom',
    schema: template.schema || { fields: [] },
    defaultSettings: template.defaultSettings || {},
    defaultVisibility: template.defaultVisibility || 'private',
    placement: componentPlacements.includes(template.placement) ? template.placement : 'profile',
    defaultLayoutWidth: componentLayoutWidths.includes(template.defaultLayoutWidth) ? template.defaultLayoutWidth : 'half',
    published: template.published !== false,
    nsfw: Boolean(template.nsfw),
    adult: Boolean(template.adult),
  };
}

export function buildUserComponentFromTemplate(template, ownerId, position = 0) {
  const normalized = normalizeComponentTemplate(template);
  return {
    ownerId,
    templateId: normalized.id,
    title: normalized.name,
    kind: normalized.kind,
    settings: { ...normalized.defaultSettings },
    visibility: normalized.defaultVisibility,
    position,
    placement: normalized.placement,
    layoutWidth: normalized.defaultLayoutWidth,
    dataSource: normalized.dataSource || 'manual',
    createdAt: null,
    updatedAt: null,
  };
}
